/**
 * A barra de filtros da grade de pinout: um seletor por campo.
 *
 * Todos são o mesmo `Multi` — clicar mostra a lista, digitar filtra por
 * pedaço, sem caixa e sem acento, e o topo oferece "selecionar todos" os que
 * estão à vista. Pino, circuito, observação e fonte eram
 * campos de texto em que era preciso acertar o valor; agora a lista mostra os
 * que existem.
 *
 * **Os filtros afunilam.** A lista de cada seletor vem dos pinouts que os
 * *outros* filtros deixam passar (`api/pinouts/opcoes`): escolhido um
 * componente, o seletor de pino só mostra os pinos dele, e o de conector só os
 * conectores dele. O próprio campo não entra — com dois pinos marcados, a
 * lista de pinos continua mostrando os outros.
 *
 * A lista só é pedida com o seletor aberto, e cada escolha aplica na hora: é
 * clique, não digitação, então não há rajada de consultas (regra 2).
 */

import { useMemo, useState } from "react"
import {
  usarChicotes, usarComponentes, usarConectores, usarCores, usarModelos, usarRestricoes,
  type Catalogo,
} from "@/api/catalogos"
import {
  usarOpcoesDoPinout, type CampoDoFiltro, type FiltroPinout, type OrdemPinout,
} from "@/api/pinouts"
import { Multi } from "@/kit/Multi"

/** O filtro como a tela o guarda: ids nos cadastros, o próprio texto em pino, observação e fonte. */
export type FiltroTela = {
  componentes: number[]
  conectores: number[]
  circuitos: number[]
  cores: number[]
  chicotes: number[]
  restricoes: number[]
  modelos: number[]
  pinos: string[]
  obs: string[]
  arquivosOriginais: string[]
  ordem: OrdemPinout
}

export const FILTRO_VAZIO: FiltroTela = {
  componentes: [], conectores: [], circuitos: [], cores: [], chicotes: [], restricoes: [], modelos: [],
  pinos: [], obs: [], arquivosOriginais: [], ordem: "recentes",
}

/** A ordem não conta como filtro: ela não esconde linha nenhuma. */
export const IGNORAR_NA_CONTAGEM = ["ordem"]

/**
 * Um filtro vindo de visão salva, no formato de hoje.
 *
 * Visão salva antes dos seletores guardava pino, observação e fonte como texto
 * com vírgula ("A01, A02") — vira a lista de valores. O circuito era o nome
 * digitado, que não dá para trocar por id sem consultar: sai do filtro.
 */
export function normalizarFiltro(f?: Partial<Record<keyof FiltroTela, unknown>> | null): FiltroTela {
  const ids = (v: unknown) =>
    Array.isArray(v) ? v.map(Number).filter((n) => Number.isFinite(n) && n > 0) : []
  const textos = (v: unknown) =>
    Array.isArray(v) ? v.map(String)
    : typeof v === "string" ? v.split(",").map((x) => x.trim()).filter(Boolean)
    : []
  const ordem = f?.ordem === "pino" || f?.ordem === "componente" ? f.ordem : "recentes"
  return {
    componentes: ids(f?.componentes), conectores: ids(f?.conectores), circuitos: ids(f?.circuitos),
    cores: ids(f?.cores), chicotes: ids(f?.chicotes), restricoes: ids(f?.restricoes), modelos: ids(f?.modelos),
    pinos: textos(f?.pinos), obs: textos(f?.obs), arquivosOriginais: textos(f?.arquivosOriginais),
    ordem,
  }
}

export function paraApi(f: FiltroTela): FiltroPinout {
  const lista = (ids: number[]) => (ids.length ? ids.join(",") : undefined)
  return {
    componentes: lista(f.componentes),
    conectores: lista(f.conectores),
    circuitos: lista(f.circuitos),
    cores: lista(f.cores),
    chicotes: lista(f.chicotes),
    restricoes: lista(f.restricoes),
    modelos: lista(f.modelos),
    pinos: f.pinos.length ? f.pinos : undefined,
    obs: f.obs.length ? f.obs : undefined,
    arquivosOriginais: f.arquivosOriginais.length ? f.arquivosOriginais : undefined,
    ordem: f.ordem,
  }
}

const SEM_CATALOGO: Catalogo = { itens: [], total: 0, completo: true, carregando: false }

export function FiltrosPinout({
  projetoId, aplicado, aoAplicar,
}: {
  projetoId: number
  aplicado: FiltroTela
  aoAplicar: (f: FiltroTela) => void
}) {
  // Os catálogos do projeto (já em cache) dão nome ao que está escolhido com o
  // seletor fechado — a visão salva abre a tela com ids, não com nomes.
  const componentes = usarComponentes(projetoId)
  const conectores = usarConectores(projetoId)
  const cores = usarCores(projetoId)
  const chicotes = usarChicotes(projetoId)
  const restricoes = usarRestricoes(projetoId)
  const modelos = usarModelos(projetoId)

  const filtro = useMemo(() => paraApi(aplicado), [aplicado])

  function mudar<C extends keyof FiltroTela>(campo: C, valor: FiltroTela[C]) {
    aoAplicar({ ...aplicado, [campo]: valor })
  }

  const comum = { projetoId, filtro }

  return (
    <div
      className="barra"
      style={{
        flex: "0 0 auto", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6,
        padding: "6px 18px", background: "var(--surface2)", borderBottom: "1px solid var(--line2)",
      }}
    >
      <SeletorDeId {...comum} campo="componente" rotulo="Componente" base={componentes}
                   escolhidos={aplicado.componentes} aoMudar={(v) => mudar("componentes", v)} largura={168} />
      <SeletorDeId {...comum} campo="conector" rotulo="Conector" base={conectores}
                   escolhidos={aplicado.conectores} aoMudar={(v) => mudar("conectores", v)} />
      <SeletorDeTexto {...comum} campo="pino" rotulo="Pino"
                      escolhidos={aplicado.pinos} aoMudar={(v) => mudar("pinos", v)} largura={110} />
      <SeletorDeId {...comum} campo="circuito" rotulo="Circuito" base={SEM_CATALOGO}
                   escolhidos={aplicado.circuitos} aoMudar={(v) => mudar("circuitos", v)} largura={130} />
      <SeletorDeId {...comum} campo="cor" rotulo="Cor" base={cores}
                   escolhidos={aplicado.cores} aoMudar={(v) => mudar("cores", v)} largura={120} />
      <SeletorDeId {...comum} campo="chicote" rotulo="Chicote" base={chicotes}
                   escolhidos={aplicado.chicotes} aoMudar={(v) => mudar("chicotes", v)} largura={130} />
      <SeletorDeId {...comum} campo="restricao" rotulo="Restrição" base={restricoes}
                   escolhidos={aplicado.restricoes} aoMudar={(v) => mudar("restricoes", v)} largura={140} />
      <SeletorDeId {...comum} campo="modelo" rotulo="Modelo" base={modelos}
                   escolhidos={aplicado.modelos} aoMudar={(v) => mudar("modelos", v)} largura={150} />
      <SeletorDeTexto {...comum} campo="obs" rotulo="Observação"
                      escolhidos={aplicado.obs} aoMudar={(v) => mudar("obs", v)} largura={130} />
      <SeletorDeTexto {...comum} campo="fonte" rotulo="Fonte"
                      escolhidos={aplicado.arquivosOriginais} aoMudar={(v) => mudar("arquivosOriginais", v)} largura={120} />

      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>Ordem</span>
        <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 7, background: "var(--surface3)" }}>
          {([["recentes", "Recentes"], ["pino", "Pino"], ["componente", "Componente"]] as const).map(([v, r]) => (
            <button
              key={v}
              onClick={() => mudar("ordem", v)}
              aria-pressed={aplicado.ordem === v}
              style={{
                height: 22, padding: "0 10px", borderRadius: 5, fontSize: 11.5,
                fontWeight: aplicado.ordem === v ? 600 : 500,
                background: aplicado.ordem === v ? "var(--surface)" : "transparent",
                color: aplicado.ordem === v ? "var(--txt)" : "var(--txt2)",
                boxShadow: aplicado.ordem === v ? "var(--sombra)" : undefined,
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

type PropsDoSeletor<K> = {
  projetoId: number
  filtro: FiltroPinout
  campo: CampoDoFiltro
  rotulo: string
  escolhidos: K[]
  aoMudar: (v: K[]) => void
  largura?: number
}

/**
 * Campo de cadastro (componente, cor…): o valor é o id. Fechado, mostra o
 * catálogo do projeto — só para dar nome ao escolhido; aberto, as opções do funil.
 */
function SeletorDeId({ base, ...p }: PropsDoSeletor<number> & { base: Catalogo }) {
  const [aberto, setAberto] = useState(false)
  const opcoes = usarOpcoesDoPinout(p.projetoId, p.campo, p.filtro, aberto)
  const catalogo: Catalogo = aberto
    ? {
        ...opcoes,
        itens: opcoes.itens.map((i) => ({ ...i, id: Number(i.id) })),
        buscar: async (t) => (await opcoes.buscar!(t)).map((i) => ({ ...i, id: Number(i.id) })),
      }
    : base
  return (
    <Multi rotulo={p.rotulo} catalogo={catalogo} escolhidos={p.escolhidos} aoMudar={p.aoMudar}
           largura={p.largura} aoAbrir={setAberto} />
  )
}

/** Campo de texto (pino, observação, fonte): o próprio valor é o filtro. */
function SeletorDeTexto(p: PropsDoSeletor<string>) {
  const [aberto, setAberto] = useState(false)
  const opcoes = usarOpcoesDoPinout(p.projetoId, p.campo, p.filtro, aberto)
  // "2" antes de "10": o banco ordena como texto, a pessoa lê como número.
  const catalogo = {
    ...opcoes,
    itens: [...opcoes.itens].sort((a, b) => a.id.localeCompare(b.id, "pt-BR", { numeric: true })),
  }
  return (
    <Multi<string> rotulo={p.rotulo} catalogo={catalogo} escolhidos={p.escolhidos} aoMudar={p.aoMudar}
                   largura={p.largura} aoAbrir={setAberto} />
  )
}
